import { string } from 'yup';

import { FILTER_DNE_OPERATOR, FILTER_EQUAL_OPERATOR } from 'constants/all';

import { isObject, isString, isPrimitiveType } from './helpers';

export const pluralRules = new Intl.PluralRules('en-US');

/**
 * Capitalize string
 * @param str
 */
export const capitalize = (str: string) =>
  str && str.charAt(0).toUpperCase() + str.slice(1);

export const paramToSearchString = (str: string) => {
  let ret = str;
  ret = ret.replace('0x20', ' ');
  ret = ret.replace('0x26', '&');
  ret = ret.replace('0x3f', '?');
  return ret;
};

export const searchStringToParam = (str: string) => {
  let ret = str;
  ret = ret.replace(' ', '0x20');
  ret = ret.replace('&', '0#26');
  ret = ret.replace('?', '0x3f');
  return ret;
};

export const toCamelCase = (str: string) => {
  if (str.includes(' ')) return str;
  return str.replace(/^([A-Z])|[\s-_](\w)/g, function (match, p1, p2) {
    if (p2) return p2.toUpperCase();
    return p1.toLowerCase();
  });
};

export const keysToCamelCase = (obj: Record<string, unknown>) => {
  return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    acc[toCamelCase(key)] = obj[key];
    return acc;
  }, {});
};

export const toSnakeCaseRigorous = (str: string) => {
  return str.replace(
    /^([A-Z])|([A-Z])|[\s-](\w)/g,
    function (match, p1, p2, p3) {
      if (p2) return '_' + p2.toLowerCase();
      if (p3) return '_' + p3.toLowerCase();
      return p1.toLowerCase();
    },
  );
};

export const toSnakeCase = (str: string) => {
  if (str.includes(' ')) return str;
  return str.replace(
    /^([A-Z])|([A-Z])|[\s-](\w)/g,
    function (match, p1, p2, p3) {
      if (p2) return '_' + p2.toLowerCase();
      if (p3) return '_' + p3.toLowerCase();
      return p1.toLowerCase();
    },
  );
};

export const toKebabCase = (str: string) => {
  return str.replace(/_/g, '-').toLowerCase();
};

export const keysToCamelCaseDeep = (obj) => {
  if (isPrimitiveType(obj)) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((thing) => keysToCamelCaseDeep(thing));
  }
  return Object.keys(obj).reduce((acc, key) => {
    if (key === 'overrides' || key === 'metadata') {
      acc[key] = obj[key];
    } else {
      if (typeof obj[key] === 'object') {
        acc[key.includes(' ') ? key : toCamelCase(key)] = keysToCamelCaseDeep(
          obj[key],
        );
      } else {
        acc[key.includes(' ') ? key : toCamelCase(key)] = obj[key];
      }
    }
    return acc;
  }, {});
};

export const keysToSnakeCase = (obj: Record<string, unknown>) => {
  return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    acc[toSnakeCase(key)] = obj[key];
    return acc;
  }, {});
};

export const keysToSnakeCaseDeep = (obj) => {
  // base case -- primitives
  if (Object(obj) !== obj) {
    return obj;
  }
  if (obj instanceof Array) {
    return obj.map((element) => keysToSnakeCaseDeep(element));
  } else {
    return Object.keys(obj).reduce((acc, key) => {
      if (key === 'overrides' || key === 'metadata') {
        acc[key] = obj[key];
      } else {
        if (obj[key] instanceof Object) {
          acc[key.includes(' ') ? key : toSnakeCase(key)] = keysToSnakeCaseDeep(
            obj[key],
          );
        } else {
          acc[key.includes(' ') ? key : toSnakeCase(key)] = obj[key];
        }
      }
      return acc;
    }, {});
  }
};

export const toReadableString = (str: string) =>
  toSnakeCase(str).replace(/_/g, ' ');

export const toCapitalizedReadableString = (str: string) => {
  const lower = toReadableString(str);
  const capitalizedArray = lower.split(' ').map((s) => capitalize(s));
  return capitalizedArray.join(' ');
};

export const toSentenceCaseString = (str: string) => {
  return (
    toReadableString(str)
      .toLowerCase()
      // match start of string or any punctuation+space, then a letter
      .replace(
        /(^|[.!?]\s*)([a-z])/g,
        (_, sep, char) => sep + char.toUpperCase(),
      )
  );
};

export const trim = (str: string, length: number) => {
  return str.length > length ? str.substring(0, length) + '...' : str;
};

export const getErrorMessage = (error, defaultMessage = null) => {
  const possibleErrors = [error];
  if (isObject(error)) {
    possibleErrors.push(error.message);
    possibleErrors.push(...Object.values(error.extra || {}));
  }
  const message =
    possibleErrors.filter((v) => v && isString(v))[0] || defaultMessage;
  return capitalize(message);
};

export const isEmptyHTML = (htmlString) => {
  const strippedHtml = htmlString.replace(/<[^>]+>/g, '').replace(/\s/g, '');
  return Boolean(strippedHtml.length);
};

export const isValidEmail = (mail) => {
  const schema = string().email();
  return schema.isValidSync(mail);
};

/**
 * Split a user-inputted quantities string into individual quantity tokens
 *
 * @param quantityString A string of comma or space separated quantities
 * @param defaultResult What should be returned if the input contains no quantities
 * @returns The split quantities as an array, or the defaultResult if the input contained no quantities
 */
export const splitQuantityString = (quantityString, defaultResult = ['1']) => {
  if (typeof quantityString !== 'string') {
    return defaultResult;
  }

  // Strip out leading and trailing commas and whitespace
  const stripped = quantityString.replace(/^[,\s]+|[,\s]+$/g, '');
  if (!stripped) {
    return defaultResult;
  }

  // Split on all inner commas and whitespace
  return stripped.split(/[,\s]+/g);
};

export { v4 as uuid } from 'uuid';

export const isEmptyString = (str) => str.trim() === '';

export const pluralizeWithoutCount = (
  singular,
  count = 0,
  plural = singular + 's',
) => {
  const grammaticalNumber = pluralRules.select(count);
  switch (grammaticalNumber) {
    case 'one':
      return singular;
    case 'other':
      return plural;
    default:
      throw new Error('Unknown: ' + grammaticalNumber);
  }
};

export const pluralize = (singular, count = 0, plural = singular + 's') => {
  return `${count} ${pluralizeWithoutCount(singular, count, plural)}`;
};

export const getHasOrHave = (objectCount) =>
  objectCount === 1 ? 'has' : 'have';

export const extractMetadataKey = (keyString) =>
  keyString.split('[')[1].slice(0, keyString.includes('__ne') ? -5 : -1);

/**
 * __ne is appended to a metadata filed to indicate that we are filtering for
 * "does not equal". The below code looks for '__ne' in the metadata keyString
 * to be able to tell what operator should be assigned to the metadata filter
 */
export const extractMetadataOperator = (keyString) =>
  keyString.includes('__ne') ? FILTER_DNE_OPERATOR : FILTER_EQUAL_OPERATOR;

export const extractKeyValueFromMetadataString = (metadataString) => {
  const [keyString, value] = metadataString.split('=');
  const operator = extractMetadataOperator(keyString);
  const key = extractMetadataKey(keyString);
  return [key, operator, value];
};

export const formatConjunction = (list) =>
  new Intl.ListFormat('en-US', {
    style: 'long',
    type: 'conjunction',
  }).format(list);

export const formatDisjunction = (clauses: string[]) =>
  new Intl.ListFormat('en-US', {
    style: 'long',
    type: 'disjunction',
  }).format(clauses);

/**
 * Calculate and return a status of where the user is in a paginated list.
 * e.g. Given `page=2`, `pageSize=5`, `totalRecords=20`, and `singularEntityName="object"`, returns `"6 - 10 of 20 objects"`
 */
export const paginationStatus = (
  page,
  pageSize,
  totalRecords,
  singularEntityName,
) => {
  const pageFirstRecord = pageSize * (page - 1) + 1;
  const pageLastRecord = Math.min(page * pageSize, totalRecords);
  return `${pageFirstRecord} - ${pageLastRecord} of ${pluralize(
    singularEntityName,
    totalRecords,
  )}`;
};
